import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from './api'

type Theme = 'light' | 'dark' | 'auto'
export type WebSearchMode = 'off' | 'auto' | 'on'

export const FONT_STEPS = [13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 28, 30] as const
export const FONT_DEFAULT_STEP = 5
export const FONT_MIN_STEP = 0
export const FONT_MAX_STEP = FONT_STEPS.length - 1

export interface ModelOption {
  id: string
  name: string
  description: string
  cost?: string
}

// Фолбэк, если бэкенд-список ещё не загрузился / недоступен. Актуальный список тянем динамически
// из GET /api/v1/models (единый источник — LLM Service model_catalog), поэтому НЕ хардкодим устаревшие.
export const AVAILABLE_MODELS: ModelOption[] = [
  { id: 'openrouter/owl-alpha', name: 'Owl Alpha', description: 'Бесплатная, основная (OpenRouter)', cost: 'free' },
]

export const DEFAULT_MODEL_ID = 'openrouter/owl-alpha'
const LEGACY_MODEL_IDS = new Set<string>(['openai/gpt-oss-120b:free'])

interface Settings {
  theme: Theme
  fontStep: number
  selectedModel: string
  webSearchMode: WebSearchMode
}

interface SettingsContextType {
  settings: Settings
  availableModels: ModelOption[]
  setTheme: (theme: Theme) => void
  setFontStep: (step: number) => void
  increaseFontStep: () => void
  decreaseFontStep: () => void
  setSelectedModel: (modelId: string) => void
  setWebSearchMode: (mode: WebSearchMode) => void
}

const STORAGE_KEY = 'miniapp_settings'

const defaultSettings: Settings = {
  theme: 'light',
  fontStep: FONT_DEFAULT_STEP,
  selectedModel: DEFAULT_MODEL_ID,
  webSearchMode: 'auto',
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const step = typeof parsed.fontStep === 'number'
        ? Math.max(FONT_MIN_STEP, Math.min(FONT_MAX_STEP, parsed.fontStep))
        : FONT_DEFAULT_STEP
      const theme = parsed.theme === 'auto' ? 'light' : (parsed.theme || defaultSettings.theme)
      const webSearchMode: WebSearchMode =
        parsed.webSearchMode === 'off' || parsed.webSearchMode === 'on' || parsed.webSearchMode === 'auto'
          ? parsed.webSearchMode
          : 'auto'
      const storedModel = parsed.selectedModel && !LEGACY_MODEL_IDS.has(parsed.selectedModel)
        ? parsed.selectedModel
        : DEFAULT_MODEL_ID
      return {
        theme,
        fontStep: step,
        selectedModel: storedModel,
        webSearchMode,
      }
    }
  } catch {
    // ignore
  }
  return defaultSettings
}

function saveSettings(settings: Settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // ignore
  }
}

function isColorDark(hex: string): boolean {
  const clean = hex.replace('#', '')
  if (clean.length < 6) return false
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128
}

function detectDarkTheme(): boolean {
  try {
    const cs = (window as any).Telegram?.WebApp?.colorScheme
    if (cs === 'dark' || cs === 'light') return cs === 'dark'
  } catch { /* ignore */ }

  const bgColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--tg-theme-bg-color').trim()
  if (bgColor && bgColor.startsWith('#')) return isColorDark(bgColor)

  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
    root.style.setProperty('--tg-theme-bg-color', '#1a1a2e')
    root.style.setProperty('--tg-theme-text-color', '#e4e4e7')
    root.style.setProperty('--tg-theme-hint-color', '#71717a')
    root.style.setProperty('--tg-theme-link-color', '#60a5fa')
    root.style.setProperty('--tg-theme-button-color', '#3b82f6')
    root.style.setProperty('--tg-theme-button-text-color', '#ffffff')
    root.style.setProperty('--tg-theme-secondary-bg-color', '#16213e')
  } else if (theme === 'light') {
    root.classList.remove('dark')
    root.style.setProperty('--tg-theme-bg-color', '#ffffff')
    root.style.setProperty('--tg-theme-text-color', '#1e293b')
    root.style.setProperty('--tg-theme-hint-color', '#94a3b8')
    root.style.setProperty('--tg-theme-link-color', '#3b82f6')
    root.style.setProperty('--tg-theme-button-color', '#3b82f6')
    root.style.setProperty('--tg-theme-button-text-color', '#ffffff')
    root.style.setProperty('--tg-theme-secondary-bg-color', '#f1f5f9')
  } else {
    root.style.removeProperty('--tg-theme-bg-color')
    root.style.removeProperty('--tg-theme-text-color')
    root.style.removeProperty('--tg-theme-hint-color')
    root.style.removeProperty('--tg-theme-link-color')
    root.style.removeProperty('--tg-theme-button-color')
    root.style.removeProperty('--tg-theme-button-text-color')
    root.style.removeProperty('--tg-theme-secondary-bg-color')
    if (detectDarkTheme()) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }
}

function applyFontSize(fontStep: number) {
  const step = Math.max(FONT_MIN_STEP, Math.min(FONT_MAX_STEP, fontStep))
  const px = FONT_STEPS[step]
  document.documentElement.style.fontSize = `${px}px`
}

const SettingsContext = createContext<SettingsContextType | null>(null)

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings)
  const [availableModels, setAvailableModels] = useState<ModelOption[]>(AVAILABLE_MODELS)

  // Динамически подтягиваем актуальный список моделей (как веб/VK). Если сохранённая модель
  // отсутствует в актуальном списке (напр. устаревшая) — переключаем на первую.
  useEffect(() => {
    let cancelled = false
    void apiClient.getModels().then((models) => {
      if (cancelled || !Array.isArray(models) || models.length === 0) return
      setAvailableModels(models)
      setSettings((prev) => {
        if (models.some((m) => m.id === prev.selectedModel)) return prev
        const next = { ...prev, selectedModel: models[0].id }
        saveSettings(next)
        return next
      })
    }).catch(() => { /* фолбэк AVAILABLE_MODELS */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    applyTheme(settings.theme)
    applyFontSize(settings.fontStep)

    if (settings.theme === 'auto') {
      const resync = () => applyTheme('auto')
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      mq.addEventListener('change', resync)
      try { (window as any).Telegram?.WebApp?.onEvent?.('themeChanged', resync) } catch { /* ignore */ }
      return () => {
        mq.removeEventListener('change', resync)
        try { (window as any).Telegram?.WebApp?.offEvent?.('themeChanged', resync) } catch { /* ignore */ }
      }
    }
  }, [settings.theme, settings.fontStep])

  const setTheme = (theme: Theme) => {
    const next = { ...settings, theme }
    setSettings(next)
    saveSettings(next)
  }

  const setFontStep = (step: number) => {
    const clamped = Math.max(FONT_MIN_STEP, Math.min(FONT_MAX_STEP, step))
    const next = { ...settings, fontStep: clamped }
    setSettings(next)
    saveSettings(next)
  }

  const increaseFontStep = () => {
    if (settings.fontStep < FONT_MAX_STEP) setFontStep(settings.fontStep + 1)
  }

  const decreaseFontStep = () => {
    if (settings.fontStep > FONT_MIN_STEP) setFontStep(settings.fontStep - 1)
  }

  const setSelectedModel = (modelId: string) => {
    const next = { ...settings, selectedModel: modelId }
    setSettings(next)
    saveSettings(next)
  }

  const setWebSearchMode = (mode: WebSearchMode) => {
    const next = { ...settings, webSearchMode: mode }
    setSettings(next)
    saveSettings(next)
  }

  return (
    <SettingsContext.Provider value={{
      settings,
      availableModels,
      setTheme,
      setFontStep,
      increaseFontStep,
      decreaseFontStep,
      setSelectedModel,
      setWebSearchMode,
    }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider')
  return ctx
}
